import { createContext, useContext, useState, useCallback, useMemo, useEffect } from "react";
import en from "./locales/en";
import hi from "./locales/hi";
import ta from "./locales/ta";
import te from "./locales/te";
import kn from "./locales/kn";
import bn from "./locales/bn";
import ml from "./locales/ml";
import mr from "./locales/mr";

const STORAGE_KEY = "fyz_locale";
const DEFAULT_LOCALE = "en";

const translations = { en, hi, ta, te, kn, bn, ml, mr };

export const LOCALES = [
  { code: "en", name: "English" },
  { code: "hi", name: "हिन्दी" },
  { code: "ta", name: "தமிழ்" },
  { code: "te", name: "తెలుగు" },
  { code: "kn", name: "ಕನ್ನಡ" },
  { code: "bn", name: "বাংলা" },
  { code: "ml", name: "മലയാളം" },
  { code: "mr", name: "मराठी" },
];

function getNestedValue(obj, path) {
  return path.split(".").reduce((cur, key) => (cur && cur[key] !== undefined ? cur[key] : undefined), obj);
}

function interpolate(str, params) {
  if (!params || typeof str !== "string") return str;
  return str.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, key) => (params[key] !== undefined ? params[key] : `{{${key}}}`));
}

function getStoredLocale() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && translations[stored]) return stored;
  } catch (_) {
    /* SSR or localStorage unavailable */
  }
  return DEFAULT_LOCALE;
}

const I18nContext = createContext(null);

export function I18nProvider({ children }) {
  const [locale, setLocaleState] = useState(getStoredLocale);

  // Mirror the active locale onto <html lang="..."> so global CSS can
  // scale Indic-script headlines back to the Latin visual weight.
  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.lang = locale;
    }
  }, [locale]);

  const setLocale = useCallback((code) => {
    if (translations[code]) {
      setLocaleState(code);
      try {
        localStorage.setItem(STORAGE_KEY, code);
      } catch (_) {
        /* ignore */
      }
    }
  }, []);

  const t = useCallback(
    (key, params) => {
      let value = getNestedValue(translations[locale], key);
      if (value === undefined && locale !== DEFAULT_LOCALE) {
        value = getNestedValue(translations[DEFAULT_LOCALE], key);
      }
      if (value === undefined) return key;
      return interpolate(value, params);
    },
    [locale]
  );

  const ctx = useMemo(
    () => ({ t, locale, setLocale, locales: LOCALES }),
    [t, locale, setLocale]
  );

  return <I18nContext.Provider value={ctx}>{children}</I18nContext.Provider>;
}

export function useTranslation() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useTranslation must be used within an I18nProvider");
  return ctx;
}
